import type { WsOpenResult } from '@onefootprint/request-types/percy';
import type { ResourceContext } from '../types';

// Live resource-creation signal. useChatWs emits here when a `create` resource frame arrives over
// the socket — which only happens for live frames, never during the `ready` history replay — and
// the chat widget's fly-to-explorer animation listens. A bus (rather than prop-threading) keeps the
// socket hook decoupled from the widget that owns the toggle + badge, and means backlog/replayed
// resources never animate because they simply never emit.
export type ArrivalOrigin = { chatSessionId?: string; isLive?: boolean };

type ArrivalListener = (key: string, kind: ResourceContext['kind'], origin: ArrivalOrigin) => void;

const listeners = new Set<ArrivalListener>();

export const notifyResourceArrival = (key: string, kind: ResourceContext['kind'], origin: ArrivalOrigin = {}) => {
  for (const listener of listeners) listener(key, kind, origin);
};

export const subscribeResourceArrival = (listener: ArrivalListener): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

// Percy asked to open a resource's page beside the chat, and waits for `answer` to say what happened.
// Emitted, like arrivals, only for live frames.
export type OpenOutcome = WsOpenResult['outcome'];

type OpenListener = (resource: ResourceContext, origin: ArrivalOrigin, answer: (outcome: OpenOutcome) => void) => void;

const openListeners = new Set<OpenListener>();

export const notifyResourceOpen = (
  resource: ResourceContext,
  origin: ArrivalOrigin,
  answer: (outcome: OpenOutcome) => void,
) => {
  for (const listener of openListeners) listener(resource, origin, answer);
};

export const subscribeResourceOpen = (listener: OpenListener): (() => void) => {
  openListeners.add(listener);
  return () => {
    openListeners.delete(listener);
  };
};
